import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Editable, Slate, withReact } from 'slate-react';
import { createEditor, Descendant, Editor, Range, Transforms } from 'slate';
import {
  EditableProps,
  RenderElementProps,
  RenderLeafProps,
} from 'slate-react/dist/components/editable';
import { CustomSlateEditor } from './models/RichTextEditor';
import { CustomElement, ElementTypes } from './models/EditorElements';
import { CustomText } from './models/EditorText';
import RichTextToolbar from './components/RichTextToolbar';
import EditorElement from './components/EditorElement';
import EditorLeaf from './components/EditorLeaf';
import {
  Field,
  FieldRenderProps,
  useField,
  UseFieldConfig,
} from 'react-final-form';
import validations from 'validations';
import TagOptions, {
  TagOption,
} from './components/TagOptions/TagOptions.component';
import EditorActions from './utils/EditorActions';
import {
  includesIgnoreCase,
  isArrowDownPressed,
  isArrowUpPressed,
  isEnterPressed,
  isEscapePressed,
  isTabPressed,
  safeParse,
  safeStringify,
} from 'utils';
import { countTextLengthFromJson } from './utils';
import { ELEMENT_TYPES } from './constants';
import { Button } from 'ncoded-component-library';
import ExpandIcon from 'icons/Expand.icon';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import RichTextEditorExpandModal from './components/RichTextEditorExpandModal';
import FieldWrapper from 'components/FieldWrapper';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './RichTextEditorField.styles.scss';

const OUR_INLINE_ELEMENTS: ElementTypes[] = [
  ELEMENT_TYPES.LINK,
  ELEMENT_TYPES.EMAIL,
  ELEMENT_TYPES.INDENT,
  ELEMENT_TYPES.OUTDENT,
  ELEMENT_TYPES.MENTION,
];

const isOurInlineElement = (element: CustomElement) =>
  OUR_INLINE_ELEMENTS.includes(element.type);

// Slate breaks if we don't have an initial value
export const RICH_TEXT_EDITOR_INITIAL_VALUE: Descendant[] = [
  { type: ELEMENT_TYPES.DIV, children: [{ text: '' }] },
];

declare module 'slate' {
  interface CustomTypes {
    Editor: CustomSlateEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

type RichTextEditorDefaultProps = {
  name: string;
  tagOptions?: TagOption[];
  showMaxLength?: boolean;
  showError?: boolean;
  additionalAction?: ReactNode;
  canExpand?: boolean;
  expandedModalLabel?: string;
} & EditableProps &
  FieldWrapperProps;

export type RichTextEditorFieldProps = RichTextEditorDefaultProps &
  UseFieldConfig<string, string>;

type RichTextEditorProps = RichTextEditorDefaultProps &
  FieldRenderProps<string, HTMLElement>;

export const RichTextEditor: React.FC<RichTextEditorProps> = (props) => {
  const {
    className,
    input,
    name,
    readOnly,
    maxLength,
    additionalAction,
    children,
    canExpand,
    expandedModalLabel,
    showError = true,
    showMaxLength = true,
    tagOptions = [],
    initialValue: initialValueProp,
    meta: { invalid, touched, error },
    onKeyDown,
    ...restOfProps
  } = props;

  const [textLength, setTextLength] = useState(0);

  const [target, setTarget] = useState<Range | null>(null);
  const [isAt, setIsAt] = useState(false);
  const [atRange, setAtRange] = useState<Range>(null);
  const [index, setIndex] = useState(0);
  const [searchString, setSearchString] = useState('');

  const [expanedModal, expanedModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames('anys-rich-text-editor', className);

  useField(input.name, {
    validate: !maxLength
      ? () => null
      : validations.richEditorMaxLength(maxLength, ' '),
  });

  const { t } = useTranslation();

  const { value, onChange, ...restOfInput } = input;

  const inputValue = value || safeStringify(RICH_TEXT_EDITOR_INITIAL_VALUE);

  const canTag = !!tagOptions?.length;

  const hasError = useMemo(
    () => showError && !!(touched && invalid && error),
    [showError, touched, invalid, error],
  );

  // Create a Slate editor object that won't change across renders.
  const editor = useMemo(() => withReact(createEditor()), []);

  const { isInline, isVoid, markableVoid } = editor;

  const filteredOptions = useMemo(
    () =>
      searchString
        ? tagOptions?.filter(
            ({ display, isRequired }) =>
              isRequired || includesIgnoreCase(display, searchString),
          )
        : tagOptions,
    [tagOptions, searchString],
  );

  editor.isInline = useCallback(
    (element: CustomElement) => {
      if (isOurInlineElement(element)) return true;

      return isInline(element);
    },
    [isInline],
  );

  editor.isVoid = useCallback(
    (element: CustomElement) => {
      return element.type === ELEMENT_TYPES.MENTION ? true : isVoid(element);
    },
    [isVoid],
  );

  editor.markableVoid = useCallback(
    (element: CustomElement) => {
      return element.type === ELEMENT_TYPES.MENTION
        ? true
        : markableVoid(element);
    },
    [markableVoid],
  );

  // Define a rendering function based on the element passed to `props`.
  const renderElement = useCallback(
    (props: RenderElementProps) => <EditorElement {...props} />,
    [],
  );

  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <EditorLeaf {...props} />,
    [],
  );

  const onTagOptionSelect = useCallback(
    (option) => {
      Transforms.select(editor, target);
      EditorActions.insertMention(editor, option, target);
      setTarget(null);
    },
    [editor, target],
  );

  const handleOnChange = useCallback(
    (value: Descendant[]) => {
      const valueAsString = safeStringify(value);

      onChange?.(valueAsString);

      // setting states needed for mentions
      const { selection } = editor;

      if (selection && Range.isCollapsed(selection)) {
        if (!isAt) {
          // if not @
          const [start] = Range.edges(selection);
          const wordBefore = Editor.before(editor, start, { unit: 'word' });
          const before = wordBefore && Editor.before(editor, wordBefore);

          const wordBeforeRange = before && Editor.range(editor, before, start);
          const charBeforeRange =
            wordBefore && Editor.range(editor, wordBefore, start);

          const beforeText = wordBeforeRange
            ? Editor.string(editor, wordBeforeRange)
            : charBeforeRange && Editor.string(editor, charBeforeRange);
          const beforeMatch = beforeText && beforeText.match(/^@(\w*)$/);

          if (beforeMatch) {
            //if @
            setIsAt(true);
            setAtRange(wordBeforeRange || charBeforeRange);
            setTarget(wordBeforeRange || charBeforeRange);
            setSearchString(beforeMatch[1] || '');
            setIndex(0);
            return;
          }
        }

        if (atRange) {
          const [start, end] = [atRange.anchor, selection];

          const atText = Editor.string(
            editor,
            Editor.range(editor, start, end),
          );

          const atTextMatch = atText?.match(/^@(\w*\s?\w*)$/);

          if (atTextMatch) {
            setTarget(Editor.range(editor, start, end));
            setSearchString(atTextMatch[1]);
            setIndex(0);
            return;
          } else {
            setIsAt(false);
            setAtRange(null);
          }
        }
      }

      setTarget(null);
    },
    [atRange, editor, isAt, onChange],
  );

  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (target && filteredOptions.length > 0) {
        if (isArrowUpPressed(event)) {
          event.preventDefault();
          const nextIndex = index <= 0 ? filteredOptions.length - 1 : index - 1;
          setIndex(nextIndex);
          return;
        }

        if (isArrowDownPressed(event)) {
          event.preventDefault();
          const prevIndex = index >= filteredOptions.length - 1 ? 0 : index + 1;
          setIndex(prevIndex);
          return;
        }

        if (isTabPressed(event) || isEnterPressed(event)) {
          event.preventDefault();
          Transforms.select(editor, target);
          EditorActions.insertMention(editor, filteredOptions[index], target);
          setTarget(null);
          return;
        }

        if (isEscapePressed(event)) {
          event.preventDefault();
          event.stopPropagation();
          setTarget(null);
          return;
        }
      }
    },
    [filteredOptions, editor, index, target],
  );

  //if there is maxLength of chars create label of remaining characters
  const maxLengthLabel = useMemo(() => {
    if (!maxLength || !showMaxLength) return <></>;

    if (maxLength - textLength >= 0)
      return (
        <small className={classNames('max-length-text')}>
          {t('charactersLeft', {
            count: maxLength - textLength,
          })}
        </small>
      );

    return (
      <small className={classNames('max-length-text', 'error')}>
        {t('charactersExceeded', {
          count: textLength - maxLength,
        })}
      </small>
    );
  }, [showMaxLength, maxLength, textLength, t]);

  //to handle char count
  useEffect(() => {
    if (value) {
      setTextLength(countTextLengthFromJson(value));
    }
  }, [editor, value]);

  useEffect(() => {
    if (!inputValue) onChange(safeStringify(RICH_TEXT_EDITOR_INITIAL_VALUE));
    else if (inputValue !== safeStringify(editor.children)) {
      try {
        const parsedValue = safeParse(inputValue);
        const point = { path: [0, 0], offset: 0 };
        editor.selection = { anchor: point, focus: point }; // clean up selection

        if (parsedValue) editor.children = parsedValue;
        else editor.children = RICH_TEXT_EDITOR_INITIAL_VALUE;
      } catch (error) {
        return;
      }
    }
  }, [editor, initialValueProp, inputValue, onChange]);

  return (
    <FieldWrapper
      className={classes}
      error={error}
      hasError={hasError}
      name={name}
      {...restOfProps}
    >
      <div
        className={classNames('anys-rich-text-editor__editor', {
          'anys-rich-text-editor__editor--error': hasError,
          'anys-rich-text-editor__editor--read-only': readOnly,
        })}
      >
        <Slate
          editor={editor}
          initialValue={RICH_TEXT_EDITOR_INITIAL_VALUE}
          onChange={handleOnChange}
        >
          <div className="anys-rich-text-editor__toolbar">
            <RichTextToolbar
              readOnly={readOnly}
              additionalAction={additionalAction}
            />
            {maxLengthLabel}
          </div>

          <Editable
            className={classNames('anys-rich-text-editor__textarea', {
              'anys-rich-text-editor__textarea--read-only': readOnly,
            })}
            name={name}
            readOnly={readOnly}
            maxLength={maxLength}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={handleOnKeyDown}
            {...restOfInput}
            {...restOfProps}
          />

          {canTag && target && (
            <TagOptions
              options={filteredOptions}
              target={target}
              searchString={searchString}
              selectedIndex={index}
              onTagOptionSelect={onTagOptionSelect}
              closeTagOptions={() => setTarget(null)}
            />
          )}
        </Slate>

        <div className="additional-content">
          {children}
          {canExpand && !readOnly && (
            <Button
              variant="icon"
              className="expand-button"
              onClick={expanedModal?.open}
            >
              <ExpandIcon />
            </Button>
          )}
        </div>

        {canExpand && !readOnly && (
          <RichTextEditorExpandModal
            ref={expanedModalRef}
            name={input.name}
            initialValue={inputValue}
            label={restOfProps.label}
            required={restOfProps.required}
            maxLength={maxLength}
            tagOptions={tagOptions}
            expandedModalLabel={expandedModalLabel}
            onChange={onChange}
          />
        )}
      </div>
    </FieldWrapper>
  );
};

const RichTextEditorField: React.FC<RichTextEditorFieldProps> = (props) => (
  <Field component={RichTextEditor} {...props} />
);

export default RichTextEditorField;
